// Supporters page — publicly-visible acknowledgment of funders and partners
// whose ongoing support enables GEMS @ The Arts programming.

import { Heart, Sparkles, Award, Landmark } from 'lucide-react';
import PageShell from '../components/PageShell';
import SEO from '../components/SEO';
import { JoyUnderline, JoySticker, JoyDotsField } from '../components/JoyAccents';
import { useI18n } from '../contexts/I18nContext';

const KAREN_SPILKA_PHOTOS = [
  {
    src: 'https://customer-assets-rejwkqb3.emergentagent.net/job_arts-gems-showcase/artifacts/ogpdg4lw_0f61d559-0acd-498c-9728-56af28254804-1_all_88828.webp',
    alt: 'Facebook post from Senate President Karen E. Spilka honoring five Framingham high-schoolers with State Senate citations for volunteer leadership at Gordon Park',
  },
  {
    src: 'https://customer-assets-rejwkqb3.emergentagent.net/job_arts-gems-showcase/artifacts/1m13v3db_1000128742.webp',
    alt: 'David Feliciano of New Image Barber Shop receiving a State Senate Official Citation for providing free back-to-school haircuts to Framingham youth',
  },
  {
    src: 'https://customer-assets-rejwkqb3.emergentagent.net/job_arts-gems-showcase/artifacts/6vgapqce_1000128743.webp',
    alt: 'Magaly Fernandez of Grace Is Sweet LLC receiving a State Senate Official Citation for community partnership and service',
  },
  {
    src: 'https://customer-assets-rejwkqb3.emergentagent.net/job_arts-gems-showcase/artifacts/zr5w31jc_0f61d559-0acd-498c-9728-56af28254804-1_all_86935.webp',
    alt: 'A GEMS youth leader holding her State Senate Official Citation at a Back-to-School community event',
  },
  {
    src: 'https://customer-assets-rejwkqb3.emergentagent.net/job_arts-gems-showcase/artifacts/fc2kr29y_0f61d559-0acd-498c-9728-56af28254804-1_all_86936.webp',
    alt: 'A young GEMS volunteer reading her State Senate citation with a proud smile',
  },
];

const SUPPORTERS = [
  {
    name: "Boston Children's Hospital",
    accent: 'coral',
    taglineKey: 'supporters.s1.tag',
    bodyKey: 'supporters.s1.body',
    body2Key: 'supporters.s1.body2',
  },
  {
    name: 'MetroWest Health Foundation',
    accent: 'teal',
    taglineKey: 'supporters.s2.tag',
    bodyKey: 'supporters.s2.body',
    body2Key: 'supporters.s2.body2',
  },
  {
    name: 'The Community Foundation for MetroWest',
    accent: 'yellow',
    taglineKey: 'supporters.s3.tag',
    bodyKey: 'supporters.s3.body',
    body2Key: 'supporters.s3.body2',
  },
  {
    name: 'The Sudbury Foundation',
    accent: 'coral',
    taglineKey: 'supporters.s4.tag',
    bodyKey: 'supporters.s4.body',
    body2Key: 'supporters.s4.body2',
  },
  {
    name: 'Health Resources in Action',
    accent: 'teal',
    taglineKey: 'supporters.s5.tag',
    bodyKey: 'supporters.s5.body',
    body2Key: 'supporters.s5.body2',
  },
  {
    name: 'MassDevelopment',
    accent: 'yellow',
    logo: '/supporters/massdev.png',
    logoAlt: 'MassDevelopment logo',
    taglineKey: 'supporters.s6.tag',
    bodyKey: 'supporters.s6.body',
  },
  {
    name: 'MA Senate President Karen E. Spilka',
    accent: 'coral',
    taglineKey: 'supporters.s7.tag',
    bodyKey: 'supporters.s7.body',
    body2Key: 'supporters.s7.body2',
    photos: KAREN_SPILKA_PHOTOS,
  },
];

const ACCENT_MAP = {
  coral: { bg: '#E5614C', bgSoft: 'rgba(229,97,76,0.08)', text: '#E5614C' },
  teal: { bg: '#5DCAB5', bgSoft: 'rgba(93,202,181,0.10)', text: '#5DCAB5' },
  yellow: { bg: '#FCD968', bgSoft: 'rgba(252,217,104,0.16)', text: '#B8912A' },
};

export default function SupportersPage() {
  const { t } = useI18n();
  return (
    <PageShell
      eyebrow={<><Heart className="h-4 w-4" fill="currentColor" /> {t('supporters.eyebrow')}</>}
      title={<>{t('supporters.title1')} <span className="italic text-[#E5614C]">{t('supporters.title2')}</span></>}
      subtitle={t('supporters.subtitle')}
      accent="coral"
    >
      <SEO
        title="Our Supporters"
        description="The foundations, elected officials, and organizations whose generous support makes GEMS @ The Arts programming possible — Boston Children's Hospital, MetroWest Health Foundation, The Community Foundation for MetroWest, The Sudbury Foundation, Health Resources in Action, MassDevelopment, and MA Senate President Karen E. Spilka."
        path="/supporters"
      />

      {/* ─── Composite logos banner ────────────────────────────────── */}
      <section className="pb-6" data-testid="supporters-logos">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="relative rounded-[28px] bg-white border border-[#2A1B1A]/8 shadow-[0_30px_60px_-30px_rgba(42,27,26,0.25)] p-6 sm:p-10 lg:p-14">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <JoySticker rotate={-3} color="yellow">
                <span className="inline-flex items-center gap-1.5">
                  <Award className="h-3.5 w-3.5" /> {t('supporters.badge')}
                </span>
              </JoySticker>
            </div>
            <img
              src="/supporters/composite-logos-v2.jpg"
              alt="Logos of GEMS @ The Arts foundation supporters: The Sudbury Foundation, Health Resources in Action, MetroWest Health Foundation, Community Foundation for MetroWest, and Boston Children's Hospital"
              loading="eager"
              className="w-full h-auto rounded-xl"
              data-testid="supporters-composite-image"
            />

            {/* Co-supporters strip — MassDev logo + Karen Spilka name flowing with the photo */}
            <div className="mt-6 sm:mt-8">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 lg:gap-14">
                <img
                  src="/supporters/massdev.png"
                  alt="MassDevelopment logo"
                  loading="lazy"
                  className="h-16 sm:h-20 lg:h-24 w-auto object-contain"
                  data-testid="supporters-massdev-logo"
                />
                <div className="hidden sm:block h-10 w-px bg-[#2A1B1A]/12" aria-hidden="true" />
                <div className="flex items-center gap-3" data-testid="supporters-karen-inline">
                  <Landmark className="h-7 w-7 text-[#E5614C] shrink-0" aria-hidden="true" />
                  <div className="text-left leading-tight">
                    <div className="font-serif-display text-[18px] sm:text-[20px] lg:text-[22px] font-bold text-[#2A1B1A]">
                      {t('supporters.karenLabel')}
                    </div>
                    <div className="text-[12px] text-[#2A1B1A]/60">
                      {t('supporters.karenSubLabel')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Full-width supporter cards ────────────────────────────── */}
      <section className="py-12 lg:py-16" data-testid="supporters-list">
        <div className="max-w-5xl mx-auto px-5 md:px-10 space-y-8 lg:space-y-10">
          {SUPPORTERS.map((s, i) => {
            const cl = ACCENT_MAP[s.accent];
            return (
              <article
                key={s.name}
                data-testid={`supporter-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="relative bg-white rounded-[24px] border border-[#2A1B1A]/8 overflow-hidden hover:shadow-[0_28px_60px_-25px_rgba(42,27,26,0.3)] transition-[box-shadow] duration-300"
              >
                {/* Left color rail */}
                <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ backgroundColor: cl.bg }} aria-hidden="true" />
                <div className="p-7 sm:p-10 lg:p-12">
                  {s.logo && (
                    <div className="mb-6 inline-flex items-center justify-start bg-white rounded-xl">
                      <img
                        src={s.logo}
                        alt={s.logoAlt || `${s.name} logo`}
                        loading="lazy"
                        className="h-14 sm:h-16 w-auto object-contain"
                      />
                    </div>
                  )}
                  {/* Number + tagline row */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <div
                      className="inline-flex items-center justify-center h-9 min-w-9 px-3 rounded-full font-serif-display font-bold text-[16px] leading-none"
                      style={{ backgroundColor: cl.bgSoft, color: cl.text }}
                      aria-hidden="true"
                    >
                      0{i + 1}
                    </div>
                    <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: cl.text }}>
                      <Sparkles className="h-3.5 w-3.5" />
                      {t(s.taglineKey)}
                    </div>
                  </div>

                  <h2 className="mt-5 font-serif-display text-[28px] sm:text-[36px] lg:text-[44px] font-bold text-[#2A1B1A] leading-[1.05]">
                    {s.name}
                  </h2>
                  <div className="mt-5 h-1 w-14 rounded-full" style={{ backgroundColor: cl.bg }} aria-hidden="true" />

                  <p className="mt-6 text-[15.5px] sm:text-[16px] text-[#2A1B1A]/78 leading-relaxed">
                    {t(s.bodyKey)}
                  </p>
                  {s.body2Key && (
                    <p className="mt-4 text-[15.5px] sm:text-[16px] text-[#2A1B1A]/78 leading-relaxed">
                      {t(s.body2Key)}
                    </p>
                  )}

                  {/* Optional embedded photo mosaic (e.g. Karen Spilka citations) */}
                  {s.photos && s.photos.length > 0 && (
                    <div className="mt-8" data-testid={`supporter-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-photos`}>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                        {s.photos.map((p, idx) => (
                          <div
                            key={idx}
                            className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-[#FAF3EE] border border-[#2A1B1A]/8 shadow-[0_10px_30px_-15px_rgba(42,27,26,0.35)]"
                          >
                            <img
                              src={p.src}
                              alt={p.alt}
                              loading="lazy"
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                      <p className="mt-4 text-[13px] text-[#2A1B1A]/60 italic leading-relaxed">
                        {t('supporters.citationsCaption')}
                      </p>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* ─── Thank-you closing note ────────────────────────────────── */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="relative rounded-[28px] bg-[#2A1B1A] text-white p-10 lg:p-14 text-center overflow-hidden">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#E5614C]/15 blur-3xl pointer-events-none" />
            <JoyDotsField className="opacity-[0.15] mix-blend-screen" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-[#FCD968] font-semibold uppercase tracking-[0.18em] text-[12px]">
                <Heart className="h-3.5 w-3.5" fill="currentColor" /> {t('supporters.thanksEyebrow')}
              </div>
              <h2 className="font-serif-display mt-4 text-[32px] sm:text-[42px] lg:text-[52px] font-bold leading-[1.05]">
                {t('supporters.thanks1')} <JoyUnderline color="#FCD968">{t('supporters.thanks2')}</JoyUnderline> {t('supporters.thanks3')}
              </h2>
              <p className="mt-5 text-white/85 text-[16px] max-w-xl mx-auto">
                {t('supporters.thanksDesc')}
              </p>
              <p className="mt-8 text-[13.5px] text-white/60">
                {t('supporters.contactPrompt')}{' '}
                <a href="mailto:info@gemsatthearts.org" className="text-[#FCD968] font-semibold hover:underline">
                  info@gemsatthearts.org
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
